// api.js
//
// Thin axios wrapper around the FastAPI backend. Change REACT_APP_API_BASE
// in a .env file at the frontend root if the backend isn't on localhost:8000,
// e.g.:
//   REACT_APP_API_BASE=http://localhost:8000

import axios from "axios";

export const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const client = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  // Required so the signed session cookie from /session/login is sent on
  // every subsequent request, and set on the response. Needed even for
  // local dev since the React dev server (:3000) and API (:8000) are
  // different origins from the browser's point of view.
  withCredentials: true,
});

// --- Dashboard accounts (this app's own login, separate from Kite) -------
export const login = (username, password) => client.post("/session/login", { username, password });
export const signup = (username, password) => client.post("/session/signup", { username, password });
export const logout = () => client.post("/session/logout");
export const getSessionStatus = () => client.get("/session/status");

// --- This user's own Kite Connect app credentials ---------------------------
export const getKiteCredentials = () => client.get("/account/kite-credentials");
export const saveKiteCredentials = (apiKey, apiSecret) =>
  client.post("/account/kite-credentials", { api_key: apiKey, api_secret: apiSecret });

// --- Kite OAuth (this user's own Zerodha session) ---------------------------
export const getLoginUrl = () => client.get("/auth/login-url");
export const completeAuth = (requestToken) =>
  client.post("/auth/complete", { request_token: requestToken });
export const getAuthStatus = () => client.get("/auth/status");

// --- Positions ------------------------------------------------------------
export const getPositions = () => client.get("/positions");

// --- Instrument lookups ---------------------------------------------------
export const getUnderlyings = () => client.get("/instruments/underlyings");
export const getExpiries = (underlying) =>
  client.get("/instruments/expiries", { params: { underlying } });

// --- Strategies -------------------------------------------------------------
export const listStrategies = () => client.get("/strategies");
export const getStrategy = (id) => client.get(`/strategies/${id}`);
export const createStrategy = (payload) => client.post("/strategies", payload);
export const updateStrategy = (id, payload) => client.put(`/strategies/${id}`, payload);
export const deleteStrategy = (id) => client.delete(`/strategies/${id}`);
export const getPnlHistory = (id) => client.get(`/strategies/${id}/pnl_history`);

// `lots`, if given, sizes just this one entry differently from the
// strategy's saved default - it's never written back to the strategy.
export const enterStrategy = (id, lots) =>
  client.post(`/strategies/${id}/enter`, lots ? { lots } : undefined);
export const exitStrategy = (id) => client.post(`/strategies/${id}/exit`);
export const resetStrategy = (id) => client.post(`/strategies/${id}/reset`);

export const trackManual = ({ name, symbols, target, stoploss, square_off_time }) =>
  client.post("/strategies/track_manual", { name, symbols, target, stoploss, square_off_time });

// --- Account-wide P&L history (all positions, date-ranged) -----------------
export const getAccountPnlHistory = (start, end) =>
  client.get("/pnl/history", { params: { start, end } });

export default client;
